import { useNavigate } from "react-router-dom";
import { useAppSelector } from "./reduxHooks";
import { selectClientValue } from "../Slices/SelectedClientSlice";
import { useEffect, useRef } from "react";

/**
 * Use this hook to redirect the current page using the navigate function, or a custom callback function when the client changes
 * @param redirect - The redirect path or function to call when the client changes
 */
export const useClientChangeCallback = (redirect?: string, callback?: () => void) => {
  const navigate = useNavigate();
  const { clientId, isLoading } = useAppSelector(selectClientValue);
  // Track the last clientId seen by THIS hook instance, rather than reading a separate
  // useLocalStorage("lastClientId") hook instance -- react-use's useLocalStorage doesn't sync
  // across hook instances within the same tab, so on a fresh page load (e.g. a deep link),
  // this hook's own copy would still read the stale/empty value written before the
  // client-resolution hook (useClients) elsewhere in the tree updates it, causing a false
  // "client changed" redirect on first mount.
  const previousClientId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (isLoading) {
      return;
    }
    // First resolution of a clientId on this page is the baseline, not a change.
    if (previousClientId.current === undefined) {
      previousClientId.current = clientId;
      return;
    }
    if (clientId !== previousClientId.current) {
      previousClientId.current = clientId;
      !!redirect && navigate(redirect);
      callback?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, isLoading]);
};
